// Catalog for the retainer builder (components/lab/RetainerBuilder.jsx).
// Colour/preset/pattern/sticker images are all self-hosted under public/lab.
// Values here (Thai names / pattern strings) match what the old system stores in
// lab.retainer, so the lab reads them unchanged.

export const PRODUCTION_TYPES = [
  { value: 'retainer', label: 'รีเทนเนอร์' },
  { value: 'ดี-aligner', label: 'ดี-aligner' },
]

// Wraparound is the default; Clear ends the flow (no colour/pattern on a clear retainer).
export const RETAINER_TYPES = ['Wraparound', 'Hawley', 'Clear']

// Ready-made designs (รีเทนเนอร์ลายพิเศษ) — choosing one ends the flow. `name` is stored
// in retainer_preset; the image is self-hosted from the clinic site (public/lab/presets).
// Sourced 2026-07-30 from smileseasons.com/retainer-diy.
export const RETAINER_PRESETS = [
  { name: 'Champions', slug: 'champions' },
  { name: 'Back to School', slug: 'back-to-school' },
  { name: 'สงกรานต์', slug: 'songkran' },
  { name: 'ป๊อปคอร์น', slug: 'popcorn' },
  { name: 'วาเลนไทน์', slug: 'valentine' },
  { name: 'After Party', slug: 'after-party' },
  { name: 'Ho Ho Ho ซานต้าคลอส', slug: 'hohoho' },
  { name: 'My Smile', slug: 'my-smile' },
  { name: 'Sunday Beach', slug: 'sunday-beach' },
  { name: 'เทรุ เทรุ', slug: 'teru' },
  { name: 'Deep Blue Sea', slug: 'deep-blue-sea' },
  { name: 'Sunny Sky', slug: 'sunny-sky' },
  { name: 'แคนดี้', slug: 'candy' },
  { name: 'Pride', slug: 'pride' },
  { name: 'กาแล็คซี่', slug: 'galaxy' },
  { name: 'น้องส้มจี๊ด', slug: 'somjeed' },
  { name: 'แตงโม', slug: 'watermelon' },
  { name: 'แก้วมังกร', slug: 'dragonfruit' },
  { name: 'กีวี', slug: 'kiwi' },
  { name: 'วุ้นใสผลไม้', slug: 'jelly' },
  { name: 'ขบวนการห้าสี', slug: 'penta' },
  { name: 'สตรอว์เบอร์รี', slug: 'strawberry' },
  { name: 'สตรอว์เบอร์รี 2', slug: 'strawberry2' },
  { name: 'น้องเสือโคร่ง', slug: 'tiger' },
  { name: 'รักเมืองไทย', slug: 'thaiflag' },
  { name: 'ยูนิคอร์น', slug: 'unicorn' },
  { name: 'น้องเต่าทอง', slug: 'ladybug' },
  { name: 'ชมพูฟ้าเสมา', slug: 'sema' },
  // Added 2026-08-18 (Ram). Artwork pulled from the clinic's own /retainer-diy/ page —
  // the same source the rest of this list came from — not from the campaign Drive
  // folders, two of which are not link-shared. `mamaew.webp` on the site is byte-for-byte
  // the file in the Drive folder, so the two are the same asset.
  // ⚠️ `name` is the value stored in lab.retainer.retainer_preset and the key existing
  // records are matched by, so these strings are Ram's verbatim and must not be tidied.
  { name: 'มะแมว', slug: 'mamaew' },
  { name: 'อุ๊ย! เชอร์รี่', slug: 'opp-cherry' },
  { name: 'Black Magic', slug: 'black-magic' },
  // Added 2026-09-10 (Ram). September Signature. Artwork from the clinic's /retainer-diy/
  // gallery (uploads/2026/09/smile-and-go-retainer.webp), same source as the rest.
  // ⚠️ The website titles this design ยิ้มปุ๊บเที่ยวปั๊บ and tags it SMILE & GO; the CRM name
  // below is Ram's — it is what goes into lab.retainer.retainer_preset, so leave it alone.
  { name: 'Around the World', slug: 'around-the-world' },
]
export const presetImg = (slug) => `/lab/presets/${slug}.png`

// Colour pattern → number of colours the user must pick. `code`/`slug` match the
// clinic site's "เลือกสไตล์แพทเทิร์น" diagrams P-01..P-08 (public/lab/patterns).
export const RETAINER_PATTERNS = [
  { code: 'P-01', slug: 'p-01', name: 'แบบมาตราฐาน 2,999', colors: 1 },
  { code: 'P-02', slug: 'p-02', name: 'แนวตั้ง 2 สี', colors: 2 },
  // ⚠️ `colors: 2`, not 3, despite the name (Ram, 2026-08-18). The artwork's own badge
  // reads 2 TONE and every other pattern's badge agrees with its count — P-03 was the
  // only one that disagreed, and it made the builder demand a third colour that the
  // pattern has nowhere to put, so บันทึก could never enable.
  // `name` is left EXACTLY as it is: it is the string written to lab.retainer
  // .retainer_pattern and the key existing records are matched by, so correcting it
  // here would orphan every P-03 already stored and diverge from the old system.
  { code: 'P-03', slug: 'p-03', name: 'แนวตั้ง 3 สี', colors: 2 },
  { code: 'P-04', slug: 'p-04', name: 'แนวตั้งเฉียง 3 สี', colors: 3 },
  { code: 'P-05', slug: 'p-05', name: 'ลายทางสลับ 2 สี', colors: 2 },
  { code: 'P-06', slug: 'p-06', name: 'แนวนอน 2 สี', colors: 2 },
  { code: 'P-07', slug: 'p-07', name: 'ไล่สีจากเข้มไปอ่อน 1 สี', colors: 1 },
  { code: 'P-08', slug: 'p-08', name: 'Random 2 สี', colors: 2 },
]
export const patternColorCount = (name) => (RETAINER_PATTERNS.find((p) => p.name === name)?.colors ?? 1)
export const patternImg = (slug) => `/lab/patterns/${slug}.png`

// Colour swatches — the clinic's actual retainer photo set (Drive: "retainer color"),
// which is the source of truth for what a colour really looks like. `name` is the Thai
// name on the photo file and is what gets stored in retainer_color[]; `code` is kept from
// the public designer so a colour means the same thing there and here. `hex` is sampled
// from the photo — used for compact chips where a full image is too big to read.
// Note: 4 codes the designer lists (Y02, Y03, R02, BK02) have no product photo and are
// deliberately absent, so the code sequence has gaps.
export const RETAINER_COLORS = [
  { code: 'Y01', name: 'เหลืองนีออน', slug: 'neon-yellow', hex: '#F7F354' },
  { code: 'O01', name: 'ส้มคอร์เดีย', slug: 'cordia-orange', hex: '#FF8D25' },
  { code: 'R01', name: 'แดงกุหลาบ', slug: 'rose-red', hex: '#E7001C' },
  { code: 'R03', name: 'แดงป๊อปปี้', slug: 'poppy-red', hex: '#F13926' },
  { code: 'P01', name: 'ชมพูบานเย็น', slug: 'magenta-pink', hex: '#FF3A95' },
  { code: 'P02', name: 'ชมพูซากุระ', slug: 'sakura-pink', hex: '#F9AFBE' },
  { code: 'G01', name: 'เขียวอ่อนใบเตย', slug: 'pandan-green', hex: '#5CD56A' },
  { code: 'G02', name: 'เขียวเข้มมรกต', slug: 'emerald-green', hex: '#00B379' },
  { code: 'B01', name: 'ฟ้าโทแพซ', slug: 'topaz-blue', hex: '#A4CEE8' },
  { code: 'B02', name: 'น้ำเงินรอยัล', slug: 'royal-blue', hex: '#3F93D4' },
  { code: 'B03', name: 'ฟ้าเทอร์ควอยซ์', slug: 'turquoise-blue', hex: '#00B3DE' },
  { code: 'BL01', name: 'น้ำเงินกรมท่า', slug: 'navy-blue', hex: '#001B42' },
  { code: 'BL02', name: 'น้ำเงินเข้ม', slug: 'deep-blue', hex: '#073FB0' },
  { code: 'PU01', name: 'ม่วงไลแลค', slug: 'lilac-purple', hex: '#5A6CEC' },
  { code: 'PU02', name: 'ม่วงศรีตรัง', slug: 'jacaranda-purple', hex: '#E1A3EA' },
  { code: 'BK01', name: 'ดำมิดไนท์', slug: 'midnight-black', hex: '#0D0E0D' },
  { code: 'C01', name: 'ใส', slug: 'clear', hex: '#DAE3DC' },
  { code: 'C02', name: 'เบจ', slug: 'beige', hex: '#F4D3BD' },
  { code: 'C03', name: 'ขาวหิมะ', slug: 'snow-white', hex: '#E1E8E5' },
]
export const colorImg = (slug) => `/lab/colors/${slug}.webp`
export const colorByName = (name) => RETAINER_COLORS.find((c) => c.name === name)

// Stickers — the same catalogue the patient picks from on the public retainer designer
// (smileseasons.com/retainer-diy-app), kept id-for-id with it so a lab sheet names the
// exact sticker the patient chose. `id` is the stable code (also the image filename);
// `name` is the site's own English label. Images self-hosted in public/lab/stickers.
// The designer gives the patient three sticker slots, so the lab caps at three too.
export const STICKER_SLOTS = 3
export const STICKER_CATEGORIES = [
  'สัตว์น้อยน่ารัก',
  'หัวใจและหน้ายิ้ม',
  'ผักผลไม้',
  'ดาว',
  'ขนมหวาน',
  'ดอกไม้และธรรมชาติ',
  'กีฬาและเกม',
  'ลายกราฟิก',
  'แฟนตาซี',
  'ดนตรี',
  'ธีมไทย',
  'ธีมญี่ปุ่น',
  'ธีมเกาหลี',
  'ธีมฝรั่งเศส',
  'ธีมอังกฤษ',
]
export const RETAINER_STICKERS = [
  { id: 'bear-cherry-1', name: 'Bear Cherry 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'bear-strawberry-1', name: 'Bear Strawberry 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'capybara-3', name: 'Capybara 3', category: 'สัตว์น้อยน่ารัก' },
  { id: 'capybara-float-1', name: 'Capybara Float 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'chick-1-1', name: 'Chick 1 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'chick-2-1', name: 'Chick 2 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'clownfish-1', name: 'Clownfish 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'dog-flying-1', name: 'Dog Flying 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'dog-sitting-1', name: 'Dog Sitting 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'dolphin-1', name: 'Dolphin 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'koala-sitting-1', name: 'Koala Sitting 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'koala-standing-1', name: 'Koala Standing 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'orange-cat-face-1', name: 'Orange Cat Face 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'orange-cat-sleeping-1', name: 'Orange Cat Sleeping 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'panda-sitting-1', name: 'Panda Sitting 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'panda-standing-1', name: 'Panda Standing 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'parrot-1', name: 'Parrot 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'penguin-party-1', name: 'Penguin Party 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'penguin-swimmer-1', name: 'Penguin Swimmer 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'rabbit-1-1', name: 'Rabbit 1 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'rabbit-2-1', name: 'Rabbit 2 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'seashell-1', name: 'Seashell 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'tiger-1', name: 'Tiger 1', category: 'สัตว์น้อยน่ารัก' },
  { id: 'grinning-face-1', name: 'Grinning Face 1', category: 'หัวใจและหน้ายิ้ม' },
  { id: 'heart-eyes-face-1', name: 'Heart Eyes Face 1', category: 'หัวใจและหน้ายิ้ม' },
  { id: 'laughing-face-1', name: 'Laughing Face 1', category: 'หัวใจและหน้ายิ้ม' },
  { id: 'pink-heart-outline-1', name: 'Pink Heart Outline 1', category: 'หัวใจและหน้ายิ้ม' },
  { id: 'red-bow-1', name: 'Red Bow 1', category: 'หัวใจและหน้ายิ้ม' },
  { id: 'red-heart-1', name: 'Red Heart 1', category: 'หัวใจและหน้ายิ้ม' },
  { id: 'ribbon-bow-1', name: 'Ribbon Bow 1', category: 'หัวใจและหน้ายิ้ม' },
  { id: 'smiley-face-1', name: 'Smiley Face 1', category: 'หัวใจและหน้ายิ้ม' },
  { id: 'smiling-face-closed-eyes-1', name: 'Smiling Face Closed Eyes 1', category: 'หัวใจและหน้ายิ้ม' },
  { id: 'teal-heart-1', name: 'Teal Heart 1', category: 'หัวใจและหน้ายิ้ม' },
  { id: 'apple-slice-1', name: 'Apple Slice 1', category: 'ผักผลไม้' },
  { id: 'apple-whole-1', name: 'Apple Whole 1', category: 'ผักผลไม้' },
  { id: 'avocado-half-1', name: 'Avocado Half 1', category: 'ผักผลไม้' },
  { id: 'avocado-whole-1', name: 'Avocado Whole 1', category: 'ผักผลไม้' },
  { id: 'banana-bunch-1', name: 'Banana Bunch 1', category: 'ผักผลไม้' },
  { id: 'carrot-1', name: 'Carrot 1', category: 'ผักผลไม้' },
  { id: 'cherry-1', name: 'Cherry 1', category: 'ผักผลไม้' },
  { id: 'durian-3', name: 'Durian 3', category: 'ผักผลไม้' },
  { id: 'durian-slice-1', name: 'Durian Slice 1', category: 'ผักผลไม้' },
  { id: 'kiwi-half-1', name: 'Kiwi Half 1', category: 'ผักผลไม้' },
  { id: 'kiwi-slice-1', name: 'Kiwi Slice 1', category: 'ผักผลไม้' },
  { id: 'lemon-slice-1', name: 'Lemon Slice 1', category: 'ผักผลไม้' },
  { id: 'lemon-whole-1', name: 'Lemon Whole 1', category: 'ผักผลไม้' },
  { id: 'mangosteen-3', name: 'Mangosteen 3', category: 'ผักผลไม้' },
  { id: 'mangosteen-half-1', name: 'Mangosteen Half 1', category: 'ผักผลไม้' },
  { id: 'orange-slice-1', name: 'Orange Slice 1', category: 'ผักผลไม้' },
  { id: 'orange-whole-1', name: 'Orange Whole 1', category: 'ผักผลไม้' },
  { id: 'peach-half-1', name: 'Peach Half 1', category: 'ผักผลไม้' },
  { id: 'peach-whole-1', name: 'Peach Whole 1', category: 'ผักผลไม้' },
  { id: 'peeled-banana-1', name: 'Peeled Banana 1', category: 'ผักผลไม้' },
  { id: 'strawberry-1', name: 'Strawberry 1', category: 'ผักผลไม้' },
  { id: 'tomato-3', name: 'Tomato 3', category: 'ผักผลไม้' },
  { id: 'tomatoes-on-vine-1', name: 'Tomatoes On Vine 1', category: 'ผักผลไม้' },
  { id: 'watermelon-round-slice-1', name: 'Watermelon Round Slice 1', category: 'ผักผลไม้' },
  { id: 'watermelon-triangle-slice-1', name: 'Watermelon Triangle Slice 1', category: 'ผักผลไม้' },
  { id: 'blue-star-1', name: 'Blue Star 1', category: 'ดาว' },
  { id: 'blue-star-splash-1', name: 'Blue Star Splash 1', category: 'ดาว' },
  { id: 'pink-star-1', name: 'Pink Star 1', category: 'ดาว' },
  { id: 'sparkle-stars-1', name: 'Sparkle Stars 1', category: 'ดาว' },
  { id: 'yellow-shooting-star-1', name: 'Yellow Shooting Star 1', category: 'ดาว' },
  { id: 'birthday-cake-blue-1', name: 'Birthday Cake Blue 1', category: 'ขนมหวาน' },
  { id: 'birthday-cake-round-1', name: 'Birthday Cake Round 1', category: 'ขนมหวาน' },
  { id: 'bread-loaf-1', name: 'Bread Loaf 1', category: 'ขนมหวาน' },
  { id: 'candy-cane-1', name: 'Candy Cane 1', category: 'ขนมหวาน' },
  { id: 'chocolate-cake-slice-1', name: 'Chocolate Cake Slice 1', category: 'ขนมหวาน' },
  { id: 'chocolate-chip-cookie-1', name: 'Chocolate Chip Cookie 1', category: 'ขนมหวาน' },
  { id: 'chocolate-cupcake-1', name: 'Chocolate Cupcake 1', category: 'ขนมหวาน' },
  { id: 'cinnamon-roll-1', name: 'Cinnamon Roll 1', category: 'ขนมหวาน' },
  { id: 'cream-cupcake-1', name: 'Cream Cupcake 1', category: 'ขนมหวาน' },
  { id: 'croissant-1', name: 'Croissant 1', category: 'ขนมหวาน' },
  { id: 'green-cake-slice-1', name: 'Green Cake Slice 1', category: 'ขนมหวาน' },
  { id: 'heart-cake-1', name: 'Heart Cake 1', category: 'ขนมหวาน' },
  { id: 'peppermint-candy-1', name: 'Peppermint Candy 1', category: 'ขนมหวาน' },
  { id: 'pink-cake-slice-1', name: 'Pink Cake Slice 1', category: 'ขนมหวาน' },
  { id: 'pink-donut-1', name: 'Pink Donut 1', category: 'ขนมหวาน' },
  { id: 'pretzel-1', name: 'Pretzel 1', category: 'ขนมหวาน' },
  { id: 'red-popsicle-1', name: 'Red Popsicle 1', category: 'ขนมหวาน' },
  { id: 'soft-serve-ice-cream-1', name: 'Soft Serve Ice Cream 1', category: 'ขนมหวาน' },
  { id: 'star-cookie-1', name: 'Star Cookie 1', category: 'ขนมหวาน' },
  { id: 'strawberry-cupcake-1', name: 'Strawberry Cupcake 1', category: 'ขนมหวาน' },
  { id: 'vanilla-donut-1', name: 'Vanilla Donut 1', category: 'ขนมหวาน' },
  { id: 'wrapped-candy-1', name: 'Wrapped Candy 1', category: 'ขนมหวาน' },
  { id: 'blue-cloud-1', name: 'Blue Cloud 1', category: 'ดอกไม้และธรรมชาติ' },
  { id: 'blue-flower-1', name: 'Blue Flower 1', category: 'ดอกไม้และธรรมชาติ' },
  { id: 'pink-cloud-1', name: 'Pink Cloud 1', category: 'ดอกไม้และธรรมชาติ' },
  { id: 'pink-flower-1', name: 'Pink Flower 1', category: 'ดอกไม้และธรรมชาติ' },
  { id: 'yellow-flower-1', name: 'Yellow Flower 1', category: 'ดอกไม้และธรรมชาติ' },
  { id: 'basketball-1', name: 'Basketball 1', category: 'กีฬาและเกม' },
  { id: 'bicycle-1', name: 'Bicycle 1', category: 'กีฬาและเกม' },
  { id: 'dice-1', name: 'Dice 1', category: 'กีฬาและเกม' },
  { id: 'game-controller-1', name: 'Game Controller 1', category: 'กีฬาและเกม' },
  { id: 'rugby-ball-1', name: 'Rugby Ball 1', category: 'กีฬาและเกม' },
  { id: 'shuttlecock-1', name: 'Shuttlecock 1', category: 'กีฬาและเกม' },
  { id: 'skateboard-1', name: 'Skateboard 1', category: 'กีฬาและเกม' },
  { id: 'soccer-ball-1', name: 'Soccer Ball 1', category: 'กีฬาและเกม' },
  { id: 'abstract-green-square-1', name: 'Abstract Green Square 1', category: 'ลายกราฟิก' },
  { id: 'abstract-orange-burst-1', name: 'Abstract Orange Burst 1', category: 'ลายกราฟิก' },
  { id: 'abstract-yellow-ring-1', name: 'Abstract Yellow Ring 1', category: 'ลายกราฟิก' },
  { id: 'rainbow-1', name: 'Rainbow 1', category: 'ลายกราฟิก' },
  { id: 'alien-head-1', name: 'Alien Head 1', category: 'แฟนตาซี' },
  { id: 'ghost-1-1', name: 'Ghost 1 1', category: 'แฟนตาซี' },
  { id: 'ghost-2-1', name: 'Ghost 2 1', category: 'แฟนตาซี' },
  { id: 'meteor-1', name: 'Meteor 1', category: 'แฟนตาซี' },
  { id: 'santa-1', name: 'Santa 1', category: 'แฟนตาซี' },
  { id: 'skull-1', name: 'Skull 1', category: 'แฟนตาซี' },
  { id: 'ufo-1', name: 'UFO 1', category: 'แฟนตาซี' },
  { id: 'cassette-tape-1', name: 'Cassette Tape 1', category: 'ดนตรี' },
  { id: 'guitar-1', name: 'Guitar 1', category: 'ดนตรี' },
  { id: 'headphones-1', name: 'Headphones 1', category: 'ดนตรี' },
  { id: 'radio-1', name: 'Radio 1', category: 'ดนตรี' },
  { id: 'vinyl-record-1', name: 'Vinyl Record 1', category: 'ดนตรี' },
  { id: 'barb-fish-1', name: 'Barb Fish', category: 'ธีมไทย' },
  { id: 'blue-tuktuk-1', name: 'Blue Tuktuk 1', category: 'ธีมไทย' },
  { id: 'coconut-water-1', name: 'Coconut Water 1', category: 'ธีมไทย' },
  { id: 'green-kite-1', name: 'Green Kite 1', category: 'ธีมไทย' },
  { id: 'longtail-boat-1', name: 'Longtail Boat 1', category: 'ธีมไทย' },
  { id: 'rainbow-kite-1', name: 'Rainbow Kite 1', category: 'ธีมไทย' },
  { id: 'red-tram-1', name: 'Red Tram 1', category: 'ธีมไทย' },
  { id: 'thai-elephant-1', name: 'Thai Elephant 1', category: 'ธีมไทย' },
  { id: 'thai-tuktuk-1', name: 'Thai Tuktuk 1', category: 'ธีมไทย' },
  { id: 'daruma-1', name: 'Daruma 1', category: 'ธีมญี่ปุ่น' },
  { id: 'fish-flag-1', name: 'Fish Flag 1', category: 'ธีมญี่ปุ่น' },
  { id: 'fox-mask-1', name: 'Fox Mask 1', category: 'ธีมญี่ปุ่น' },
  { id: 'lucky-cat-1', name: 'Lucky Cat 1', category: 'ธีมญี่ปุ่น' },
  { id: 'mount-fuji-1', name: 'Mount Fuji 1', category: 'ธีมญี่ปุ่น' },
  { id: 'octopus-1', name: 'Octopus 1', category: 'ธีมญี่ปุ่น' },
  { id: 'sakura-1', name: 'Sakura 1', category: 'ธีมญี่ปุ่น' },
  { id: 'salmon-sushi-1', name: 'Salmon Sushi 1', category: 'ธีมญี่ปุ่น' },
  { id: 'shrimp-sushi-1', name: 'Shrimp Sushi 1', category: 'ธีมญี่ปุ่น' },
  { id: 'grilled-pork-1', name: 'Grilled Pork 1', category: 'ธีมเกาหลี' },
  { id: 'hand-fan-1', name: 'Hand Fan 1', category: 'ธีมเกาหลี' },
  { id: 'kimbap-roll-1', name: 'Kimbap Roll 1', category: 'ธีมเกาหลี' },
  { id: 'kimchi-soup-1', name: 'Kimchi Soup 1', category: 'ธีมเกาหลี' },
  { id: 'palace-1', name: 'Palace 1', category: 'ธีมเกาหลี' },
  { id: 'soju-1', name: 'Soju 1', category: 'ธีมเกาหลี' },
  { id: 'tteokbokki-skewers-1', name: 'Tteokbokki Skewers 1', category: 'ธีมเกาหลี' },
  { id: 'arc-de-triomphe-1', name: 'Arc De Triomphe 1', category: 'ธีมฝรั่งเศส' },
  { id: 'brown-macaron-1', name: 'Brown Macaron 1', category: 'ธีมฝรั่งเศส' },
  { id: 'cheese-1', name: 'Cheese 1', category: 'ธีมฝรั่งเศส' },
  { id: 'coffee-cup-1', name: 'Coffee Cup 1', category: 'ธีมฝรั่งเศส' },
  { id: 'eiffel-tower-1', name: 'Eiffel Tower 1', category: 'ธีมฝรั่งเศส' },
  { id: 'iced-coffee-1', name: 'Iced Coffee 1', category: 'ธีมฝรั่งเศส' },
  { id: 'perfume-1', name: 'Perfume 1', category: 'ธีมฝรั่งเศส' },
  { id: 'pink-macaron-1', name: 'Pink Macaron 1', category: 'ธีมฝรั่งเศส' },
  { id: 'wine-bottle-1', name: 'Wine Bottle 1', category: 'ธีมฝรั่งเศส' },
  { id: 'big-ben-1', name: 'Big Ben 1', category: 'ธีมอังกฤษ' },
  { id: 'british-soldier-1', name: 'British Soldier 1', category: 'ธีมอังกฤษ' },
  { id: 'double-decker-bus-1', name: 'Double Decker Bus 1', category: 'ธีมอังกฤษ' },
  { id: 'ferris-wheel-1', name: 'Ferris Wheel 1', category: 'ธีมอังกฤษ' },
  { id: 'paint-palette-1', name: 'Paint Palette 1', category: 'ธีมอังกฤษ' },
  { id: 'phone-booth-1', name: 'Phone Booth 1', category: 'ธีมอังกฤษ' },
  { id: 'pineapple-jam-jar-1', name: 'Pineapple Jam Jar 1', category: 'ธีมอังกฤษ' },
  { id: 'strawberry-jam-jar-1', name: 'Strawberry Jam Jar 1', category: 'ธีมอังกฤษ' },
  { id: 'teapot-1', name: 'Teapot 1', category: 'ธีมอังกฤษ' },
]
export const stickerImg = (id) => `/lab/stickers/${id}.webp`
export const stickerById = (id) => RETAINER_STICKERS.find((s) => s.id === id)

// The lab detail (retainer config) is editable only while the lab is still at the first
// stage — "รอส่ง LAB" — before anything is sent out.
export const EDIT_DETAIL_STAGE = 'รอส่ง LAB'
